import { decode } from 'html-entities';
import DOMPurify, { Config } from 'isomorphic-dompurify';

import { RECAPTCHA_V3_SITEKEY } from '@/constants/env';

import { ReCaptchaAction, ReCaptchaResponse } from '../models/authModels';
import { AmbaAlertTypes, ServerStatus } from '../models/globalModels';
import { RewardTypeEnum } from '../models/rewardsModel';

const DEFAULT_SANITIZATION_OPTIONS: Config = {
  ADD_ATTR: ['target', 'rel'],
};

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node instanceof Element && node.hasAttribute('target')) {
    // This force every link to open in a new tab and avoid the possibility of phishing
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener');
  }
});

// sanitizes HTML code coming from DB/User provided and decodes HTML entities if needed
export const sanitizeHtml = (dirtyHtml: string, options?: Config, decodeHtml?: boolean) => {
  const clean = DOMPurify.sanitize(dirtyHtml, { ...DEFAULT_SANITIZATION_OPTIONS, ...options });
  return decodeHtml ? decode(clean) : clean;
};

// converts ServerStatus into Alert type so that it renders the intended style
export const getAlertTypeByStatus = (status: ServerStatus): AmbaAlertTypes | undefined => {
  if (!status) return;
  switch (status) {
    case ServerStatus.FETCH:
      return AmbaAlertTypes.SUCCESS;
    case ServerStatus.FETCH_ERROR:
      return AmbaAlertTypes.ERROR;
    default:
      return AmbaAlertTypes.NOTIFICATION;
  }
};

export const isReCaptchaValid = async (action: ReCaptchaAction): Promise<ReCaptchaResponse> => {
  //@ts-ignore
  if (typeof window?.grecaptcha === 'undefined') return;
  //@ts-ignore
  const token = await grecaptcha?.enterprise?.execute(RECAPTCHA_V3_SITEKEY, { action: action.toString() });
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, action: action }),
  };
  // internal Portal API call
  let response = await fetch('/api/recaptcha', options);
  let recaptcha = (await response.json()) as ReCaptchaResponse;
  const score = recaptcha?.score;
  const status = recaptcha?.status;
  recaptcha.isValid = status === 'Ok' && score > 0.5;
  recaptcha.token = token;
  return recaptcha;
};

export const generateRequestId = (): string => {
  let d: any = new Date().getTime();
  const baseId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return baseId.replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const isTangoCard = (type: RewardTypeEnum) => {
  return [RewardTypeEnum.CASH, RewardTypeEnum.PREPAID, RewardTypeEnum.TANGO].includes(type);
};

export const normalizeHost = (h?: string) =>
  h
    ?.trim()
    .replace(/^[a-z]+:\/\//i, '')
    .split('/')[0]
    .replace(/:\d+$/, '')
    .toLowerCase() || undefined;
